/* ============================================================
   AGILITE QR LIVE CAMERA  — ISOLATED asset
   ------------------------------------------------------------
   In-page camera for the receipt step (getUserMedia viewfinder
   with a live receipt-edge highlight + manual capture). Exists
   because launching the OS camera app via a file-input intent is
   UNRELIABLE on some Androids — Motorola's camera app throws
   "Unable to complete previous operation due to low memory" on
   the browser->camera handoff (device quirk, fires regardless of
   the page's actual footprint). Streaming the camera INTO the
   page never leaves the tab, so that failure mode cannot happen.

   Behaviour:
     - AGBQrLive.available(): getUserMedia exists (and not opted
       out via window.__AGB_LIVECAM_OFF).
     - AGBQrLive.open(onBlob): fullscreen viewfinder; when the
       auto-crop stack (AGBQrCrop/jscanify/OpenCV) becomes ready
       a live quad is drawn over the detected receipt (~5 fps on
       a 480px probe — enhancement only, capture never waits for
       it); Capture hands a JPEG Blob to onBlob and closes;
       Cancel/stream-failure closes and resolves false so the
       caller can fall back to the file input.
     - Camera permission denied / no camera / any error => the
       caller falls back to the old <input capture> path.

   Memory notes: probe canvas <=480px, capture canvas <=2000px
   long side, tracks stopped on close/pagehide. No app switch =
   the page keeps its state.
   ============================================================ */
(function () {
  'use strict';

  var PROBE_DIM = 640, PROBE_MS = 180, CAPTURE_DIM = 2000, JPEG_Q = 0.92;
  var SMOOTH = 0.45;   // corner lerp between frames — steadies the live outline

  var _ui = null;       // {host, video, overlay, stream, timer}

  function available() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia &&
              !window.__AGB_LIVECAM_OFF);
  }

  function stopStream() {
    if (_ui && _ui.stream) {
      try { _ui.stream.getTracks().forEach(function (t) { t.stop(); }); } catch (e) {}
    }
  }

  function close() {
    if (!_ui) return;
    stopStream();
    if (_ui.timer) window.clearInterval(_ui.timer);
    if (_ui.host && _ui.host.parentNode) _ui.host.parentNode.removeChild(_ui.host);
    _ui = null;
  }
  window.addEventListener('pagehide', close);
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) close();          // never hold the camera in background
  });

  // ---- live edge highlight (best-effort; capture never depends on it) ----
  // Detection = AGBQrCrop.findCorners (the proper scanner pipeline: adaptive
  // Canny -> convex-quad only, bright-region fallback) — NOT jscanify's
  // biggest-raw-contour, which tracked table edges and shadows on phones.
  function startProbe() {
    var cvReady = false;
    if (window.AGBQrCrop && window.AGBQrCrop.ensureCv && window.AGBQrCrop.findCorners) {
      window.AGBQrCrop.ensureCv().then(function () {
        if (!_ui) return;
        cvReady = true;
      }).catch(function () { /* plain viewfinder */ });
    }
    var probe = document.createElement('canvas');
    var last = null;      // smoothed corners carried between frames
    var streak = 0;       // consecutive plausible detections (show hysteresis)
    var misses = 0;       // consecutive misses (hide hysteresis)
    function lerp(a, b) { return { x: a.x + (b.x - a.x) * SMOOTH, y: a.y + (b.y - a.y) * SMOOTH }; }

    // The biggest Canny contour is often NOT a document (a shadow, the table
    // edge, a bright patch) — drawing those was the "very buggy" outline.
    // Only quads that could plausibly BE a receipt get drawn, and only after
    // 2 consecutive hits; 4 straight misses clear the overlay.
    function plausible(c, w, h) {
      var p = [c.topLeftCorner, c.topRightCorner, c.bottomRightCorner, c.bottomLeftCorner];
      var area = 0;
      for (var i = 0; i < 4; i++) {
        var q = p[(i + 1) % 4];
        area += p[i].x * q.y - q.x * p[i].y;
      }
      area = Math.abs(area / 2);
      var ratio = area / (w * h);
      if (ratio < 0.06 || ratio > 0.96) return false;
      var minEdge = Infinity;
      for (var j = 0; j < 4; j++) {
        var n = p[(j + 1) % 4];
        minEdge = Math.min(minEdge, Math.hypot(p[j].x - n.x, p[j].y - n.y));
      }
      return minEdge > 0.08 * Math.max(w, h);
    }
    _ui.timer = window.setInterval(function () {
      if (!_ui || !cvReady) return;
      var v = _ui.video;
      var vw = v.videoWidth, vh = v.videoHeight;
      if (!vw || !vh) return;
      var f = Math.min(1, PROBE_DIM / Math.max(vw, vh));
      probe.width = Math.round(vw * f); probe.height = Math.round(vh * f);
      probe.getContext('2d').drawImage(v, 0, 0, probe.width, probe.height);
      var corners = null;
      try {
        var c = window.AGBQrCrop.findCorners(probe);
        if (c && plausible(c, probe.width, probe.height)) corners = c;
      } catch (e) { /* skip this frame */ }

      if (corners) {
        streak++; misses = 0;
        if (last) {
          corners = { topLeftCorner: lerp(last.topLeftCorner, corners.topLeftCorner),
                      topRightCorner: lerp(last.topRightCorner, corners.topRightCorner),
                      bottomLeftCorner: lerp(last.bottomLeftCorner, corners.bottomLeftCorner),
                      bottomRightCorner: lerp(last.bottomRightCorner, corners.bottomRightCorner) };
        }
        last = corners;
        if (streak >= 2) drawQuad(corners, probe.width, probe.height);
      } else {
        streak = 0; misses++;
        if (misses >= 4) { last = null; drawQuad(null, probe.width, probe.height); }
      }
    }, PROBE_MS);
  }

  function drawQuad(corners, pw, ph) {
    if (!_ui) return;
    var v = _ui.video, o = _ui.overlay;
    var box = v.getBoundingClientRect();
    // object-fit: contain — map video pixels onto the displayed box
    var vw = v.videoWidth, vh = v.videoHeight;
    if (!vw || !vh) return;
    var scale = Math.min(box.width / vw, box.height / vh);
    var dw = vw * scale, dh = vh * scale;
    var ox = (box.width - dw) / 2, oy = (box.height - dh) / 2;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    o.width = Math.round(box.width * dpr); o.height = Math.round(box.height * dpr);
    o.style.width = box.width + 'px'; o.style.height = box.height + 'px';
    var ctx = o.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, box.width, box.height);
    if (!corners) return;
    var k = scale * (vw / pw);            // probe px -> displayed px
    var pts = [corners.topLeftCorner, corners.topRightCorner,
               corners.bottomRightCorner, corners.bottomLeftCorner];
    ctx.strokeStyle = '#92ae74'; ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(ox + pts[0].x * k, oy + pts[0].y * k);
    for (var i = 1; i < 4; i++) ctx.lineTo(ox + pts[i].x * k, oy + pts[i].y * k);
    ctx.closePath(); ctx.stroke();
  }

  function capture(onBlob) {
    if (!_ui) return;
    var v = _ui.video;
    var vw = v.videoWidth, vh = v.videoHeight;
    if (!vw || !vh) return;
    var f = Math.min(1, CAPTURE_DIM / Math.max(vw, vh));
    var c = document.createElement('canvas');
    c.width = Math.round(vw * f); c.height = Math.round(vh * f);
    c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
    close();                               // free the camera before encode
    c.toBlob(function (b) {
      if (b) onBlob(new File([b], 'receipt.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', JPEG_Q);
  }

  // open(onBlob) -> Promise<boolean opened>. false => caller falls back.
  function open(onBlob) {
    if (!available() || _ui) return Promise.resolve(false);
    return navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: { ideal: 'environment' },
               width: { ideal: 1920 }, height: { ideal: 1080 } }
    }).then(function (stream) {
      var host = document.createElement('div');
      host.className = 'aglc';
      host.innerHTML =
        '<video class="aglc-video" playsinline autoplay muted></video>' +
        '<canvas class="aglc-overlay"></canvas>' +
        '<p class="aglc-hint">Fit the whole receipt in the frame.</p>' +
        '<div class="aglc-row">' +
          '<button type="button" class="aglc-btn" data-act="shoot">Capture</button>' +
          '<button type="button" class="aglc-btn aglc-btn--secondary" data-act="cancel">Cancel</button>' +
        '</div>';
      document.body.appendChild(host);
      var video = host.querySelector('video');
      video.srcObject = stream;
      _ui = { host: host, video: video, overlay: host.querySelector('canvas'),
              stream: stream, timer: null };
      host.querySelector('[data-act="shoot"]').addEventListener('click', function () { capture(onBlob); });
      host.querySelector('[data-act="cancel"]').addEventListener('click', close);
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
      startProbe();
      return true;
    }).catch(function () { return false; });   // denied / no camera -> fallback
  }

  // NB: fully self-contained styling. The theme's .ag-btn rules are scoped to
  // #agb-qr-app, and this overlay lives on <body> — relying on them rendered
  // the buttons as the browser's default gray (looked disabled; wasn't).
  var css =
    '.aglc{position:fixed;inset:0;z-index:2147483000;background:#000;display:flex;flex-direction:column;align-items:center;justify-content:center;}' +
    '.aglc-video{position:absolute;inset:0;width:100%;height:100%;object-fit:contain;}' +
    '.aglc-overlay{position:absolute;inset:0;margin:auto;pointer-events:none;}' +
    '.aglc-hint{position:absolute;top:18px;left:0;right:0;text-align:center;color:#fff;font-size:13px;letter-spacing:.06em;text-shadow:0 1px 3px rgba(0,0,0,.8);margin:0;padding:0 16px;font-family:"Helvetica Neue","Segoe UI",Arial,sans-serif;}' +
    '.aglc-row{position:absolute;bottom:26px;left:0;right:0;display:flex;gap:10px;justify-content:center;padding:0 20px;}' +
    '.aglc-btn{min-width:130px;display:inline-flex;align-items:center;justify-content:center;padding:15px 22px;' +
      'font-family:"Helvetica Neue","Segoe UI",Arial,sans-serif;font-size:12px;font-weight:600;letter-spacing:.16em;' +
      'text-transform:uppercase;color:#fff;background:#8f1814;border:none;border-radius:4px;cursor:pointer;-webkit-appearance:none;appearance:none;}' +
    '.aglc-btn:active{background:#741310;}' +
    '.aglc-btn--secondary{color:#fff;background:rgba(0,0,0,.45);border:1px solid #fff;}' +
    '.aglc-btn--secondary:active{background:rgba(255,255,255,.2);}';
  var tag = document.createElement('style');
  tag.textContent = css;
  document.head.appendChild(tag);

  window.AGBQrLive = { available: available, open: open, close: close };
})();
